import { useQuery } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import {
  StripboardTableScene,
  StripboardTableDayBreak,
  StripboardTableBanner,
} from "components/dashboard/body/projects/panels/schedule/types";
import { USER_API } from "config";
import {
  formatBEDateToShortDateString,
  instanceOfStripboardTableDayBreak,
  normalizeId,
} from "utils";
import { scheduleKeys } from "../../queries";

export type StripboardData = StripboardTableScene | StripboardTableDayBreak | StripboardTableBanner;

const normalizeStripboardDaybreakDates = (items: StripboardData[]): StripboardData[] =>
  items.map((item) => {
    if (!instanceOfStripboardTableDayBreak(item) || item.date == null) {
      return item;
    }
    const normalizedDate = formatBEDateToShortDateString(item.date);
    if (!normalizedDate || normalizedDate === item.date) {
      return item;
    }
    return { ...item, date: normalizedDate };
  });

interface UseStripboardListResult {
  stripboardData: StripboardData[] | undefined;
  boneyardData: (StripboardTableScene | StripboardTableDayBreak)[] | undefined;
  isStripboardLoading: boolean;
  isBoneyardLoading: boolean;
  isStripboardError: boolean;
  isBoneyardError: boolean;
  stripboardError: Error | null;
  boneyardError: Error | null;
  refetchStripboard: () => Promise<any>;
  refetchBoneyard: () => Promise<any>;
}

interface UseStripboardListParams {
  projectId: string | number;
  documentId: string | number;
  axios: AxiosInstance;
  hasValidIds: boolean;
  isBoneyard: boolean;
  scheduleStatus: boolean;
  enabled: boolean;
}

export const useStripboardList = ({
  projectId,
  documentId,
  axios,
  hasValidIds,
  isBoneyard,
  scheduleStatus,
  enabled,
}: UseStripboardListParams): UseStripboardListResult => {
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);

  const stripboardQuery = useQuery<StripboardData[]>(
    scheduleKeys.stripboard(pid, did),
    async () => {
      if (!hasValidIds || !projectId || !documentId) throw new Error("IDs not ready");
      const res = await axios.get(USER_API.getOrAddStripboard(pid, did));
      const rows = normalizeStripboardDaybreakDates(res.data as StripboardData[]);
      rows.forEach((data, index) => {
        (data as StripboardData & { Id: number }).Id = index;
      });
      return rows;
    },
    {
      enabled: enabled && hasValidIds && !isBoneyard && scheduleStatus,
      onError: () => {
        // Error handling is done in the component
      },
    }
  );

  const boneyardQuery = useQuery<StripboardTableScene[] | StripboardTableDayBreak[]>(
    scheduleKeys.boneyard(pid, did),
    async () => {
      if (!hasValidIds || !projectId || !documentId) throw new Error("IDs not ready");
      const res = await axios.get(USER_API.getBoneyardData(pid, did));
      res.data.forEach((data: any, index: number) => {
        data.Id = index;
      });
      return res.data;
    },
    {
      enabled: enabled && hasValidIds && isBoneyard && scheduleStatus,
      onError: () => {
        // Error handling is done in the component
      },
    }
  );

  return {
    stripboardData: stripboardQuery.data,
    boneyardData: boneyardQuery.data,
    // A first fetch in flight. Not `isLoading`: in React Query v4 a disabled query (no schedule
    // status row yet) stays in status "loading" forever, which kept pages on a loader.
    isStripboardLoading: stripboardQuery.isInitialLoading,
    isBoneyardLoading: boneyardQuery.isInitialLoading,
    isStripboardError: stripboardQuery.isError,
    isBoneyardError: boneyardQuery.isError,
    stripboardError: stripboardQuery.error as Error | null,
    boneyardError: boneyardQuery.error as Error | null,
    refetchStripboard: stripboardQuery.refetch,
    refetchBoneyard: boneyardQuery.refetch,
  };
};
